import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
/**
 * `/futures/allocation` datasheet view.
 */
import { useCallback, useLayoutEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import { useDebounce } from '../../hooks/useDebounce'
import { titleCaseName } from '../../utils/text'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useDeleteFuturesAllocationRowMutation,
  useGetFuturesAllocationsQuery,
  useLazyGetFuturesAllocationsNoalcQuery,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import FuturesAllocationFormDialog from './FuturesAllocationFormDialog'
import { useFuturesColumns } from './useFuturesColumns'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresFormDialogTitleTextSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
} from './futuresAccessUi'

const STORAGE_COLS_ALLOCATION = 'futures.allocation'
const STORAGE_COLS_ALLOCATION_NOALC = 'futures.allocation.noalc'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
}

/** Thứ tự ControlSource giống CSV (date → … → person_updated); nhãn giống Access Detail. */
const COLUMNS_STANDARD: ColumnDef[] = [
  { key: 'date', label: 'Date', kind: 'date' },
  { key: 'contractId', label: 'Contract ID' },
  { key: 'ps', label: 'P/S', filter: 'checkboxes' },
  { key: 'cp', label: 'CP', filter: 'checkboxes', proper: true },
  { key: 'kg', label: 'Kg', kind: 'number', align: 'right' },
  { key: 'type', label: 'Type', filter: 'checkboxes' },
  { key: 'lotAllocating', label: 'Lot Allocating', kind: 'number', align: 'right' },
  { key: 'futuresId', label: 'Futures ID' },
  { key: 'buysell', label: 'Buy/Sell', filter: 'checkboxes' },
  { key: 'month', label: 'Month', filter: 'checkboxes' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'balance', label: 'Balance', kind: 'number', align: 'right' },
  { key: 'partner', label: 'Partner', filter: 'checkboxes' },
  { key: 'dateUpdated', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdated', label: 'Updated Person' },
]

/** `frmFuturesQryAllocation_noalc` — ColumnOrder trong CSV: 1 contract_no … 4 cp_name … 7 balance_lot. */
const COLUMNS_NOALC: ColumnDef[] = [
  { key: 'contractNo', label: 'ContractNo' },
  { key: 'contractDate', label: 'ContractDate', kind: 'date' },
  { key: 'cpCode', label: 'CPCode' },
  { key: 'cpName', label: 'CPName' },
  { key: 'contractType', label: 'ContractType' },
  { key: 'quantityKg', label: 'QuantityKg', kind: 'number', align: 'right' },
  { key: 'balanceLot', label: 'BalanceLot', kind: 'number', align: 'right' },
]

/** Cột freeze khi scroll ngang — main allocation: contractId; no-alc sub: contractNo. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractId', 'contractNo'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  contractId: 140,
  contractNo: 140,
}

/** Cộng dồn chỉ số có nghĩa; giá không cộng. */
const NON_SUM_STANDARD = new Set<string>(['price'])

const sumNumericOnPage = (pageRows: AllocGridRow[], col: ColumnDef, nonSum: Set<string>): number | null => {
  if (col.kind !== 'number' || nonSum.has(col.key)) return null
  let total = 0
  let count = 0
  for (const row of pageRows) {
    const v = row[col.key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      count += 1
    }
  }
  return count > 0 ? total : null
}

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function cellValue(row: Record<string, unknown>, col: ColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  if (col.proper) return titleCaseName(v)
  return String(v)
}

const BALANCE_KEYS = new Set<string>(['balance', 'balanceLot'])

function isNegativeBalanceCell(row: Record<string, unknown>, col: ColumnDef): boolean {
  if (!BALANCE_KEYS.has(col.key)) return false
  const v = row[col.key]
  return typeof v === 'number' && Number.isFinite(v) && v < 0
}

type FilterKind = 'search' | 'date' | 'ps' | 'buysell' | 'month' | 'cp' | 'partner' | 'type'

const toIsoDateOnly = (value: unknown): string | null => {
  if (!value) return null
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return null
  const yyyy = d.getFullYear()
  const mm = `${d.getMonth() + 1}`.padStart(2, '0')
  const dd = `${d.getDate()}`.padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const collectDistinct = (rows: ReadonlyArray<Record<string, unknown>>, key: string): string[] => {
  const set = new Set<string>()
  for (const r of rows) {
    const v = r[key]
    if (v == null) continue
    const s = String(v).trim()
    if (!s) continue
    set.add(s)
  }
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

const collectDistinctDates = (rows: ReadonlyArray<Record<string, unknown>>): string[] => {
  const set = new Set<string>()
  for (const r of rows) {
    const iso = toIsoDateOnly(r['date'])
    if (iso) set.add(iso)
  }
  return [...set].sort().reverse()
}

interface Props {
  /** Giữ prop để tương thích; pilot `/futures/allocation` luôn gọi với `false` (full chiều cao). */
  embedded?: boolean
}

type AllocGridRow = Record<string, unknown> & { id: string }

export default function FuturesAllocationListView({ embedded: _embedded }: Props) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [noAlcModalOpen, setNoAlcModalOpen] = useState(false)
  const [noAlcSelectedRowId, setNoAlcSelectedRowId] = useState<string | null>(null)
  const [noAlcSearch, setNoAlcSearch] = useState('')
  // Search bar cho modal Check Alc (server-paginated) — chỉ 1 nút Search kiểu
  // convert-table; term vẫn feed thẳng server query qua `noAlcSearch`.
  const noAlcFilterPopover = useFilterPopover<'search'>()
  const debouncedNoAlcSearch = useDebounce(noAlcSearch, 350)
  const [noAlcPagination, setNoAlcPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })

  const [pagination, setPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()
  const [deleteAlloc, setDeleteAlloc] = useState<{ date: string; contractId: string; futuresId: string } | null>(null)

  const [allocUi, setAllocUi] = useState<{
    open: boolean
    mode: 'new' | 'edit'
    editRow: AllocGridRow | null
    presetContractNo: string | null
    presetBalanceLot: string | null
  }>({ open: false, mode: 'new', editRow: null, presetContractNo: null, presetBalanceLot: null })

  const queryArgs = {
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  }
  const { data, isLoading, isFetching, error, refetch } = useGetFuturesAllocationsQuery(queryArgs)

  const [fetchNoAlc, noAlcResult] = useLazyGetFuturesAllocationsNoalcQuery()

  useLayoutEffect(() => {
    if (!noAlcModalOpen) return
    void fetchNoAlc({
      page: noAlcPagination.page + 1,
      pageSize: noAlcPagination.pageSize,
      q: debouncedNoAlcSearch.trim() || undefined,
    })
  }, [noAlcModalOpen, noAlcPagination.page, noAlcPagination.pageSize, debouncedNoAlcSearch, fetchNoAlc])

  const [deleteRow, deleteState] = useDeleteFuturesAllocationRowMutation()

  const columns = COLUMNS_STANDARD

  const columnsCtrl = useFuturesColumns({
    mode: 'allocation',
    allColumns: columns,
    storageKey: STORAGE_COLS_ALLOCATION,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.allocation')
  const { getWidth: getNoAlcWidth, startResize: startNoAlcResize } = useColumnWidths('futures.allocation.noalc')

  const baseRows = useMemo(
    (): AllocGridRow[] =>
      (data?.items ?? []).map((r, idx) => ({
        ...(r as Record<string, unknown>),
        id: `${idx}-${String((r as Record<string, unknown>).contractId ?? '')}-${idx}`,
      })),
    [data?.items],
  )

  /* ── Filter pipeline: search ∧ per-column ▽ ─────────────────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, COLUMNS_STANDARD) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const displayedRows = useMemo((): AllocGridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  const rows = displayedRows

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () =>
      colPopover
        ? COLUMNS_STANDARD.find((c) => c.key === colPopover.key) ?? null
        : null,
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const noAlcColumns = COLUMNS_NOALC

  const noAlcColumnsCtrl = useFuturesColumns({
    mode: 'allocation-noalc',
    allColumns: noAlcColumns,
    storageKey: STORAGE_COLS_ALLOCATION_NOALC,
  })
  const { visibleColumns: noAlcVisibleColumns, openMenu: openNoAlcColMenu } = noAlcColumnsCtrl
  const noAlcRows = useMemo(
    (): AllocGridRow[] =>
      (noAlcResult.data?.items ?? []).map((r, idx) => ({
        ...(r as Record<string, unknown>),
        id: `noalc-${noAlcPagination.page}-${idx}-${String((r as Record<string, unknown>).contractNo ?? '')}`,
      })),
    [noAlcResult.data?.items, noAlcPagination.page],
  )

  const noAlcTotalRowValues = useMemo(() => {
    const nonSum = new Set<string>()
    return new Map(noAlcVisibleColumns.map((col) => [col.key, sumNumericOnPage(noAlcRows, col, nonSum)] as const))
  }, [noAlcRows, noAlcVisibleColumns])

  const noAlcMinTableWidth = noAlcVisibleColumns.length * 96


  const firstSelectedRow = useMemo((): AllocGridRow | null => {
    if (!selectedRowId) return null
    return baseRows.find((r) => r.id === selectedRowId) ?? null
  }, [baseRows, selectedRowId])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length

  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  const totalRowValues = useMemo(() => {
    return new Map(
      visibleColumns.map((col) => [col.key, sumNumericOnPage(displayedRows, col, NON_SUM_STANDARD)] as const),
    )
  }, [displayedRows, visibleColumns])

  const gridKey = `${pagination.page}:${pagination.pageSize}:${displayedRows.length}`


  const openNoAlcModal = () => {
    setNoAlcSearch('')
    setNoAlcPagination({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
    setNoAlcSelectedRowId(null)
    setNoAlcModalOpen(true)
  }

  const allocateFromNoAlc = useCallback(() => {
    if (!noAlcSelectedRowId) {
      snack.warning('Please choose a record!')
      return
    }
    const row = noAlcRows.find((r) => r.id === noAlcSelectedRowId)
    const contractNo = row ? str(row['contractNo']).trim() : ''
    if (!contractNo) {
      snack.warning('Please choose a record!')
      return
    }
    // GIỮ modal Check Alc MỞ (user request): sau khi allocate xong ở form nhập,
    // quay lại đây chọn contract kế tiếp → allocate nhiều lần liên tiếp. Chỉ clear
    // selection; KHÔNG đóng modal.
    setNoAlcSelectedRowId(null)
    setAllocUi({
      open: true,
      mode: 'new',
      editRow: null,
      presetContractNo: contractNo,
      presetBalanceLot: row ? str(row['balanceLot']) : null,
    })
  }, [noAlcSelectedRowId, noAlcRows, snack])

  const noAlcGridKey = `${noAlcPagination.page}:${noAlcPagination.pageSize}:${noAlcRows.length}`

  const openNew = useCallback(() => {
    setAllocUi({ open: true, mode: 'new', editRow: null, presetContractNo: null, presetBalanceLot: null })
  }, [])

  const openEdit = useCallback(() => {
    const r = firstSelectedRow
    // Access btnEdit_Click safety net: IsNull(contract_id) → "Please choose a record!".
    if (!r) {
      snack.warning('Please choose a record!')
      return
    }
    setAllocUi({ open: true, mode: 'edit', editRow: r, presetContractNo: null, presetBalanceLot: null })
  }, [firstSelectedRow, snack])

  const confirmRemoveAlloc = async () => {
    if (!deleteAlloc) return
    try {
      await deleteRow({
        date: deleteAlloc.date,
        contractId: deleteAlloc.contractId,
        futuresId: deleteAlloc.futuresId,
      }).unwrap()
      snack.success('Allocation deleted.')
      setSelectedRowId(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const removeClick = () => {
    const r = firstSelectedRow
    if (!r?.['date'] || !r['contractId'] || !r['futuresId']) return
    const d = str(r['date']).slice(0, 10)
    setDeleteAlloc({
      date: d,
      contractId: String(r['contractId']),
      futuresId: String(r['futuresId']),
    })
  }

  const minTableWidth = visibleColumns.length * 96

  return (
    <>
      {/* `& > .app-dataframe` (direct child) chỉ fill bảng list CHÍNH; modal app-dataframe
          nằm trong <Dialog> (portal) nên không bị ảnh hưởng. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                 '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' } }}>
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              {/* Access Form_Load: SCREEN_ACCESS <> 1 disables btnNew/btnEdit/btnDelete/btnCheckAlc
                  → all gate on canEdit. Per-button row guards mirror each btn*_Click verbatim
                  (btnEdit/btnDelete need a selected subform row; btnNew/btnCheckAlc need none). */}
              <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
              <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!canEdit || !firstSelectedRow} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !firstSelectedRow}
                onClick={() => removeClick()}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
              <Button size="small" variant="outlined" disabled={!canEdit} onClick={openNoAlcModal} sx={actionToolbarBtnSx} startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />} title="Check - No Alc">
                <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Check - No Alc</Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>No Alc</Box>
              </Button>
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="Contract, futures, partner…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexWrap: 'wrap',
              minHeight: 24,
              py: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>

          <DataframeBody loading={isLoading} fetching={isFetching}>
            {error ? (
              <BrandState
                tone="error"
                title="Failed to load allocation data"
                description="Please try again later."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && rows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : displayedRows.length === 0 ? (
              <BrandState
                title="No data"
                description={
                  hasActiveFilters
                    ? 'No rows match the current filters on this page. Try clearing filters or switching pages.'
                    : 'Change the search filters or open Check - No Alc to see contracts without allocation.'
                }
              />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={displayedRows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {visibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            // Cho phép bôi đen + copy tên cột (header).
                            userSelect: 'text',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {visibleColumns.map((col, index) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const total = totalRowValues.get(col.key)
                      const val = total == null ? '' : formatAccessNumber(total)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const negativeTotal =
                        BALANCE_KEYS.has(col.key) && typeof total === 'number' && Number.isFinite(total) && total < 0
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            ...(negativeTotal && { color: 'error.main' }),
                          }}
                        >
                          {index === 0 ? 'Total' : val}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: AllocGridRow) =>
                  visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const val = cellValue(row, col)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    const wrap = getWidth(col.key) != null
                    const negative = isNegativeBalanceCell(row, col)
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            ...(negative && { color: 'error.main', fontWeight: 600 }),
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            py: 0.5,
                            verticalAlign: 'top',
                          }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'top',
                          ...(negative && { color: 'error.main', fontWeight: 600 }),
                        }}
                      >
                        <Box
                          component="span"
                          className="app-table-cell-text"
                          title={val || '-'}
                          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }
                components={{
                  Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                  TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                  TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                  TableRow: (props: any) => {
                    const idx = Number((props as Record<string, unknown>)['data-item-index'])
                    const r = Number.isFinite(idx) ? displayedRows[idx] : null
                    const rid = r?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rid != null && rid === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (!rid) return
                          toggleSelectRow(rid)
                        }}
                      />
                    )
                  },
                }}
              />
            )}
          </DataframeBody>
        </Box>
      </Box>

      <Dialog
        open={noAlcModalOpen}
        onClose={() => setNoAlcModalOpen(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresFormDialogTitleSx}>
          <Typography component="span" noWrap title="Futures Control - Check Alc" sx={futuresFormDialogTitleTextSx}>
            Futures Control - Check Alc
          </Typography>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={!canEdit}
              onClick={() => allocateFromNoAlc()}
              sx={futuresFormDialogActionBtnSx}
            >
              Allocate
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setNoAlcModalOpen(false)}
              sx={futuresFormDialogActionBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            ...futuresSaleContractDialogContentSx,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Box sx={{ display: 'none' }}>
            <Checkbox checked readOnly />
          </Box>
          <FilterBar
            hasActiveFilters={!!noAlcSearch.trim()}
            onClearAll={() => {
              setNoAlcSearch('')
              setNoAlcPagination((p) => ({ ...p, page: 0 }))
              noAlcFilterPopover.close()
            }}
            activeFilterCount={noAlcSearch.trim() ? 1 : 0}
          >
            <FilterButton
              label="Search"
              active={!!noAlcSearch.trim()}
              onClick={(e) => noAlcFilterPopover.open('search', e)}
            />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openNoAlcColMenu(e.currentTarget)}
              sx={filterBarBtnSx}
              title="Columns"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
            </Button>
          </FilterBar>

          <FilterPopover
            open={noAlcFilterPopover.state !== null}
            anchorEl={noAlcFilterPopover.state?.anchor ?? null}
            onClose={() => noAlcFilterPopover.close()}
          >
            {noAlcFilterPopover.isOpen('search') && (
              <SearchFilterContent
                value={noAlcSearch}
                onChange={(v) => {
                  setNoAlcSearch(v)
                  setNoAlcPagination((p) => ({ ...p, page: 0 }))
                }}
                placeholder="Contract, partner…"
                onClear={() => noAlcFilterPopover.close()}
              />
            )}
          </FilterPopover>
          <FuturesColumnsMenu controller={noAlcColumnsCtrl} />
          <Box className="app-dataframe" sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
              className="app-dataframe-toolbar"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                flexWrap: 'wrap',
                minHeight: 24,
                py: 0,
                mb: 0.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
                {(noAlcResult.isLoading || noAlcResult.isFetching) && <CircularProgress size={15} />}
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
                  Total: {noAlcResult.data?.total ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
                  On page: {noAlcRows.length}
                </Typography>
                <TablePagination
                  component="div"
                  count={noAlcResult.data?.total ?? 0}
                  page={noAlcPagination.page}
                  onPageChange={(_, newPage) => setNoAlcPagination((p) => ({ ...p, page: newPage }))}
                  rowsPerPage={noAlcPagination.pageSize}
                  onRowsPerPageChange={(e) => {
                    setNoAlcPagination({ page: 0, pageSize: Number(e.target.value) })
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                  labelRowsPerPage="Rows"
                  sx={{
                    flexShrink: 0,
                    '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                    '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                    '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
                  }}
                />
              </Stack>
            </Box>
            <Box sx={{ height: 'min(480px, 65vh)', width: '100%', overflow: 'hidden' }}>
              {noAlcResult.isError ? (
                <BrandState
                  tone="error"
                  title="Failed to load No Alc"
                  description="Please retry or close this modal."
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        void fetchNoAlc({
                          page: noAlcPagination.page + 1,
                          pageSize: noAlcPagination.pageSize,
                          q: debouncedNoAlcSearch.trim() || undefined,
                        })
                      }
                    >
                      Retry
                    </Button>
                  }
                />
              ) : (noAlcResult.isUninitialized || noAlcResult.isLoading) && noAlcRows.length === 0 ? (
                <BrandState tone="loading" title="Loading…" description="Please wait." />
              ) : noAlcRows.length === 0 ? (
                <BrandState title="No No-Alc rows" description="Try changing the search or pagination." />
              ) : (
                <TableVirtuoso
                  key={noAlcGridKey}
                  style={{ height: '100%' }}
                  data={noAlcRows}
                  fixedHeaderContent={() => (
                    <TableRow>
                      {noAlcVisibleColumns.map((col) => {
                        const isFrozen = FROZEN_KEYS.has(col.key)
                        const w = getNoAlcWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                        const headerStyle: CSSProperties = {}
                        if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                        if (isFrozen) {
                          headerStyle.position = 'sticky'
                          headerStyle.top = 0
                          headerStyle.left = 0
                          headerStyle.zIndex = 100
                        }
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align}
                            className="app-table-header-cell"
                            style={Object.keys(headerStyle).length ? headerStyle : undefined}
                            sx={{
                              whiteSpace: 'nowrap',
                              // Cho phép bôi đen + copy tên cột (header).
                              userSelect: 'text',
                              ...(isFrozen ? {} : { position: 'relative' }),
                            }}
                          >
                            {col.label}
                            <ResizeHandle onMouseDown={(e) => startNoAlcResize(col.key, e)} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}
                  fixedFooterContent={() => (
                    <TableRow>
                      {noAlcVisibleColumns.map((col, index) => {
                        const isFrozen = FROZEN_KEYS.has(col.key)
                        const total = noAlcTotalRowValues.get(col.key)
                        const val = total == null ? '' : formatAccessNumber(total)
                        const w = getNoAlcWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                        const negativeTotal =
                          BALANCE_KEYS.has(col.key) && typeof total === 'number' && Number.isFinite(total) && total < 0
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              py: 0.5,
                              bgcolor: 'background.paper',
                              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              ...(negativeTotal && { color: 'error.main' }),
                            }}
                          >
                            {index === 0 ? 'Total' : val}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}
                  itemContent={(_: number, row: AllocGridRow) =>
                    noAlcVisibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const val = cellValue(row, col)
                      const w = getNoAlcWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const wrap = getNoAlcWidth(col.key) != null
                      const negative = isNegativeBalanceCell(row, col)
                      if (wrap) {
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{
                              ...(negative && { color: 'error.main', fontWeight: 600 }),
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              py: 0.5,
                              verticalAlign: 'top',
                            }}
                          >
                            <Box component="span" className="app-table-cell-wrap">
                              {val || '-'}
                            </Box>
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'top',
                            ...(negative && { color: 'error.main', fontWeight: 600 }),
                          }}
                        >
                          <Box
                            component="span"
                            className="app-table-cell-text"
                            title={val || '-'}
                            sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    })
                  }
                  components={{
                    Table: (props: any) => (
                      <Table {...props} size="small" stickyHeader sx={{ minWidth: noAlcMinTableWidth, width: '100%' }} />
                    ),
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                    TableRow: (props: any) => {
                      const idx = Number((props as Record<string, unknown>)['data-item-index'])
                      const r = Number.isFinite(idx) ? noAlcRows[idx] : null
                      const rid = r?.id ?? null
                      return (
                        <TableRow
                          {...props}
                          hover
                          selected={rid != null && rid === noAlcSelectedRowId}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (!rid) return
                            // Toggle: click hàng đang chọn → bỏ chọn (chuẩn convert-table §6.5).
                            setNoAlcSelectedRowId((prev) => (prev === rid ? null : rid))
                          }}
                        />
                      )
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteAlloc != null}
        title="Delete Allocation"
        danger
        message={deleteAlloc ? `Soft-delete allocation ${deleteAlloc.contractId} / ${deleteAlloc.futuresId}?` : ''}
        loading={deleteState.isLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onClose={() => setDeleteAlloc(null)}
        onCancel={() => setDeleteAlloc(null)}
        onConfirm={confirmRemoveAlloc}
      />

      <FuturesAllocationFormDialog
        open={allocUi.open}
        onClose={() => {
          setAllocUi((s) => ({ ...s, open: false }))
          setSelectedRowId(null)
        }}
        mode={allocUi.mode}
        sourceRow={allocUi.mode === 'edit' ? allocUi.editRow : null}
        presetContractNo={allocUi.mode === 'new' ? allocUi.presetContractNo : null}
        presetBalanceLot={allocUi.mode === 'new' ? allocUi.presetBalanceLot : null}
        canEdit={canEdit}
      />
    </>
  )
}
